import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Form, Header } from 'semantic-ui-react'
import {
    FormDropdownField,
    FormDateField,
    FormTextAreaFormField,
    SubmitCancelButtons,
    AppPrimaryButton,
} from 'elements'
import {
    FlexRowBetween,
    FlexRow,
    AppTable,
    AppEditButton,
    AppRemoveButtonPopup,
    FlexBox,
    LoaderBar,
    FileUploadPicker,
} from 'components'
import { DesktopMode, MobileMode } from 'layout'
import { DatePickerTypes } from 'utility/constant'
import { CaretLeft } from 'phosphor-react'
import styled from 'styled-components'
import {
    useAsset,
    useAssetInspection,
    useAssetActivity,
    useAssetInspectionItem,
    useAssetInspectionMedia,
} from 'hooks/asset-manager'
import { useAuth } from 'context/user'
import { getAssetPermissions } from 'utility/asset-permissions'
import { InspectionService } from 'utility/services/inspection'

const ScheduleInspectionContainer = styled.div`
    padding: 20px;
    margin-top: 20px;
`

const ScheduleInspectionSchema = yup.object().shape({
    AssetName: yup.string().required('Asset Name is required'),
    AssignedInspector: yup.string().required('Assigned Inspector is required'),
    InspectionType: yup.string().required('Inspection Type is required'),
    InspectionDate: yup.date().required('Inspection Date is required').nullable(),
    DueDate: yup.date().required('Due Date is required').nullable(),
    Notes: yup.string().nullable(),
})

// Inspector options - TODO: Replace with actual user list API
const inspectorOptions = [
    { key: '1', text: 'John Doe', value: 1 },
    { key: '2', text: 'Jane Smith', value: 2 },
    { key: '3', text: 'Mike Johnson', value: 3 },
]

const inspectionTypeOptions = [
    { key: '1', text: 'Routine Inspection', value: 'Routine' },
    { key: '2', text: 'Safety Inspection', value: 'Safety' },
    { key: '3', text: 'Maintenance Inspection', value: 'Maintenance' },
]

export const ScheduleAssetInspectionScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { user } = useAuth()
    const permissions = getAssetPermissions(user)
    const { captureActivity } = useAssetActivity()
    const [loading, setLoading] = useState(false)
    const [editingInspectionId, setEditingInspectionId] = useState(0)
    const [mediaFiles, setMediaFiles] = useState([])

    const assetFromState = location.state?.asset

    const { assets, fetchAssets } = useAsset()
    const {
        inspections,
        loading: inspectionLoading,
        fetchInspectionsByAsset,
        getInspectionsByAsset,
        getInspection,
        saveInspection,
        deleteInspection,
    } = useAssetInspection()
    const { saveInspectionItem } = useAssetInspectionItem()
    const { saveInspectionMedia } = useAssetInspectionMedia()

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(ScheduleInspectionSchema),
        defaultValues: {
            AssetName: assetFromState?.AssetId || '',
            AssignedInspector: '',
            InspectionType: '',
            InspectionDate: null,
            DueDate: null,
            Notes: '',
        },
    })

    const selectedAssetId = watch('AssetName')

    useEffect(() => {
        if (user?.CompanyId) {
            fetchAssets({ OnlyActive: true })
        }
    }, [user?.CompanyId, fetchAssets])

    useEffect(() => {
        const assetId = selectedAssetId || assetFromState?.AssetId
        if (assetId && user?.CompanyId) {
            fetchInspectionsByAsset(assetId)
        }
    }, [
        selectedAssetId,
        assetFromState?.AssetId,
        user?.CompanyId,
        fetchInspectionsByAsset,
    ])

    const assetsForUi = Array.isArray(assets) ? assets : []
    const inspectionsForUi = Array.isArray(inspections) ? inspections : []

    const assetOptions = useMemo(
        () =>
            assetsForUi.map(asset => ({
                key: asset.AssetId?.toString(),
                text: `${asset.AssetName} (${asset.AssetTag || asset.AssetId})`,
                value: asset.AssetId,
            })),
        [assetsForUi],
    )

    const resetForm = useCallback(
        ({ keepAssetId } = {}) => {
            setEditingInspectionId(0)
            setMediaFiles([])
            reset({
                AssetName: keepAssetId || assetFromState?.AssetId || '',
                AssignedInspector: '',
                InspectionType: '',
                InspectionDate: null,
                DueDate: null,
                Notes: '',
            })
        },
        [assetFromState?.AssetId, reset],
    )

    const handleMediaUpload = useCallback(newFiles => {
        if (!Array.isArray(newFiles) || newFiles.length === 0) return
        setMediaFiles(prev => [...prev, ...newFiles])
    }, [])

    const handleMediaRemove = useCallback(fileToRemove => {
        setMediaFiles(prev =>
            prev.filter(file => file.id !== fileToRemove.id),
        )
    }, [])

    const handleEdit = useCallback(
        async item => {
            if (!item || !permissions.canScheduleInspection) return
            const inspectionId = item.AssetInspectionId || item.Id || 0
            if (!inspectionId) return

            const inspection = await getInspection(inspectionId)
            const source = inspection || item
            let inspectionDate = null
            let dueDate = null

            if (source.InspectionDate) {
                inspectionDate = new Date(source.InspectionDate)
            } else if (item.inspectionDate) {
                inspectionDate = new Date(item.inspectionDate)
            }

            if (source.DueDate) {
                dueDate = new Date(source.DueDate)
            } else if (item.dueDate && item.dueDate !== '-') {
                dueDate = new Date(item.dueDate)
            }

            setEditingInspectionId(
                source.AssetInspectionId || source.Id || inspectionId,
            )
            reset({
                AssetName: source.AssetId || item.AssetId || assetFromState?.AssetId || '',
                AssignedInspector:
                    source.InspectedByUserId || item.assignedInspector || '',
                InspectionType:
                    source.InspectionType || item.inspectionType || '',
                InspectionDate: inspectionDate,
                DueDate: dueDate,
                Notes: source.Notes || item.notes || '',
            })
        },
        [
            assetFromState?.AssetId,
            getInspection,
            permissions.canScheduleInspection,
            reset,
        ],
    )

    const upcomingInspectionsData = useMemo(
        () =>
            inspectionsForUi.map(inspection => ({
                Id: inspection.AssetInspectionId || inspection.Id,
                AssetInspectionId:
                    inspection.AssetInspectionId || inspection.Id,
                companyId: inspection.CompanyId,
                AssetId: inspection.AssetId,
                assetName:
                    assetsForUi.find(
                        asset =>
                            Number(asset.AssetId) === Number(inspection.AssetId),
                    )?.AssetName ||
                    assetFromState?.AssetName ||
                    'Asset',
                inspectedByUserId: inspection.InspectedByUserId || '-',
                inspectionDate: inspection.InspectionDate || '-',
                dueDate: inspection.DueDate || '-',
                status: inspection.Status || '-',
                assignedInspector: inspection.InspectedByUserId || '',
                inspectionType: inspection.InspectionType || '',
                notes: inspection.Notes || '',
            })),
        [inspectionsForUi, assetsForUi, assetFromState],
    )

    const handleDelete = useCallback(
        async item => {
            if (!item || !permissions.canScheduleInspection) return
            const inspectionId = item.AssetInspectionId || item.Id || 0
            if (!inspectionId) return

            const success = await deleteInspection(inspectionId)
            if (success) {
                captureActivity({
                    assetId: item.AssetId || selectedAssetId || assetFromState?.AssetId,
                    action: 'Inspection deleted',
                })

                const assetIdToRefresh =
                    item.AssetId || selectedAssetId || assetFromState?.AssetId
                if (assetIdToRefresh) {
                    await fetchInspectionsByAsset(assetIdToRefresh)
                }

                if (editingInspectionId === inspectionId) {
                    resetForm({ keepAssetId: assetIdToRefresh })
                }
            }
        },
        [
            permissions.canScheduleInspection,
            deleteInspection,
            captureActivity,
            selectedAssetId,
            assetFromState?.AssetId,
            editingInspectionId,
            fetchInspectionsByAsset,
            resetForm,
        ],
    )

    const submit = async data => {
        if (!permissions.canScheduleInspection) return
        setLoading(true)
        try {
            const inspectionData = {
                AssetInspectionId: editingInspectionId || 0,
                AssetId: data.AssetName,
                InspectionDate:
                    data.InspectionDate?.toISOString().split('T')[0] || null,
                DueDate: data.DueDate?.toISOString().split('T')[0] || null,
                InspectedByUserId: data.AssignedInspector,
                InspectionType: data.InspectionType,
                Status: 'Scheduled',
                OverallResult: '',
                Notes: data.Notes || '',
                IsActive: true,
            }

            const result = await saveInspection(inspectionData)
            const isSaveSuccess =
                result !== null && result !== undefined && result !== false
            if (isSaveSuccess) {
                let inspectionId =
                    (typeof result === 'object'
                        ? result.AssetInspectionId || result.Id
                        : result) || editingInspectionId

                // Some backends return no ID on save; resolve it from fresh list
                // so item/media APIs can still execute.
                if (!inspectionId) {
                    const latestInspections = await getInspectionsByAsset(
                        data.AssetName,
                    )
                    inspectionId =
                        latestInspections?.[0]?.AssetInspectionId ||
                        latestInspections?.[0]?.Id ||
                        0
                }

                if (inspectionId) {
                    await saveInspectionItem({
                        AssetInspectionItemId: 0,
                        AssetInspectionId: inspectionId,
                        ItemKey: 'schedule_note',
                        ItemLabel: 'Scheduling Note',
                        AnswerType: 'Text',
                        AnswerValue: data.Notes || '',
                        Score: 0,
                        IsFailed: false,
                        DisplayOrder: 1,
                        CreatedByUserId: user?.UserId || 0,
                        CreatedOn: new Date().toISOString(),
                    })
                    const filesToUpload = mediaFiles
                        .map(mediaFile => mediaFile?.file?.file || mediaFile?.file || mediaFile)
                        .filter(file => file instanceof File)

                    await Promise.all(
                        filesToUpload.map(async actualFile => {
                            const uploadResponse = await InspectionService.uploadMedia({
                                media: actualFile,
                            })
                            return saveInspectionMedia({
                                AssetInspectionMediaId: 0,
                                AssetInspectionId: inspectionId,
                                AssetInspectionItemId: 0,
                                FileName: actualFile.name || '',
                                FileKey:
                                    uploadResponse?.CloudImageId ||
                                    uploadResponse?.FileKey ||
                                    '',
                                SignedUrl:
                                    uploadResponse?.ImagePathUrl ||
                                    uploadResponse?.FileURL ||
                                    '',
                                ContentType: actualFile.type || '',
                                FileSizeBytes: actualFile.size || 0,
                                Caption: data.Notes || actualFile.name || '',
                                CreatedByUserId: user?.UserId || 0,
                                CreatedOn: new Date().toISOString(),
                                IsActive: true,
                            })
                        }),
                    )
                }

                captureActivity({
                    assetId: data.AssetName,
                    action: editingInspectionId
                        ? 'Inspection schedule updated'
                        : 'Inspection scheduled',
                })

                // Clear the full form after successful submit.
                resetForm()
                await fetchInspectionsByAsset(data.AssetName)
            }
        } catch (error) {
            console.error('Error scheduling inspection:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleCancel = () => {
        navigate('/app/asset-management')
    }

    const handleViewAll = () => {
        console.log('View All clicked')
    }

    const upcomingInspectionsColumns = useMemo(
        () => [
            {
                key: 'assetName',
                title: 'Asset Name',
                textAlign: 'left',
            },
            {
                key: 'inspectionDate',
                title: 'Inspection Date',
                textAlign: 'left',
            },
            {
                key: 'dueDate',
                title: 'Due Date',
                textAlign: 'left',
            },
            {
                key: 'inspectedByUserId',
                title: 'Inspector',
                textAlign: 'left',
            },
            {
                key: 'status',
                title: 'Status',
                textAlign: 'left',
            },
            {
                key: 'actions',
                title: 'Actions',
                textAlign: 'center',
                render: item => (
                    <FlexBox
                        className="justify-content-center"
                        onClick={event => event.stopPropagation()}
                    >
                        <AppEditButton onClick={() => handleEdit(item)} />
                        <AppRemoveButtonPopup onConfirm={() => handleDelete(item)} />
                    </FlexBox>
                ),
            },
        ],
        [handleDelete, handleEdit],
    )

    return (
        <ScheduleInspectionContainer>
            <DesktopMode>
                <Header as="h4" block>
                    <FlexRowBetween>
                        <FlexRow className="align-items-center">
                            <FlexRow
                                onClick={() => navigate('/app/asset-management')}
                                className="align-items-center pointer me-3"
                            >
                                <CaretLeft size={24} />
                                <div className="ms-2">Back</div>
                            </FlexRow>
                            <div style={{ fontSize: '24px', fontWeight: 600 }}>
                                Schedule Asset Inspection
                            </div>
                        </FlexRow>
                    </FlexRowBetween>
                </Header>

                <div className="mb-3">
                    <p
                        style={{
                            color: '#666',
                            fontSize: '14px',
                            marginBottom: '20px',
                        }}
                    >
                        Setup an inspection for one or more assets.
                    </p>

                    <Form noValidate>
                        <Form.Group widths="equal">
                            <FormDropdownField
                                control={control}
                                name="AssetName"
                                label="Asset Name"
                                placeholder="Select Asset"
                                errors={errors.AssetName}
                                options={assetOptions}
                                required
                                disabled={!permissions.canScheduleInspection}
                            />
                            <FormDropdownField
                                control={control}
                                name="AssignedInspector"
                                label="Assigned Inspector"
                                placeholder="Select Inspector"
                                errors={errors.AssignedInspector}
                                options={inspectorOptions}
                                required
                                disabled={!permissions.canScheduleInspection}
                            />
                            <FormDropdownField
                                control={control}
                                name="InspectionType"
                                label="Inspection Type"
                                placeholder="Select Inspection Type"
                                errors={errors.InspectionType}
                                options={inspectionTypeOptions}
                                required
                                disabled={!permissions.canScheduleInspection}
                            />
                        </Form.Group>
                        <Form.Group widths="equal">
                            <FormDateField
                                control={control}
                                name="InspectionDate"
                                label="Inspection Date"
                                errors={errors.InspectionDate}
                                type={DatePickerTypes.Single}
                                required
                                disabled={!permissions.canScheduleInspection}
                            />
                            <FormDateField
                                control={control}
                                name="DueDate"
                                label="Due Date"
                                errors={errors.DueDate}
                                type={DatePickerTypes.Single}
                                required
                                disabled={!permissions.canScheduleInspection}
                            />
                        </Form.Group>
                        <Form.Group>
                            <FormTextAreaFormField
                                control={control}
                                name="Notes"
                                label="Notes"
                                placeholder="Add any additional notes here."
                                errors={errors.Notes}
                                height={100}
                                disabled={!permissions.canScheduleInspection}
                            />
                        </Form.Group>
                        <Form.Group>
                            <label style={{ fontWeight: 600, marginBottom: 8 }}>
                                Media
                            </label>
                            <FileUploadPicker
                                files={mediaFiles}
                                maxFiles={10}
                                remove
                                onRemove={handleMediaRemove}
                                addedNewFiles={handleMediaUpload}
                                disabled={!permissions.canScheduleInspection}
                                uploadButtonText="Upload Picture"
                            />
                        </Form.Group>
                        <div className="pt-1">
                            <SubmitCancelButtons
                                onSubmit={handleSubmit(submit)}
                                onCancel={resetForm}
                                className=""
                                disabled={!permissions.canScheduleInspection}
                                submitText={editingInspectionId ? 'Update' : 'Submit'}
                            />
                        </div>
                    </Form>

                    <div className="my-4">
                        <FlexRowBetween style={{ marginBottom: '16px' }}>
                            <h3
                                style={{
                                    margin: 0,
                                    fontSize: '18px',
                                    fontWeight: 600,
                                }}
                            >
                                Upcoming Inspections
                            </h3>
                            <a
                                href="#"
                                onClick={e => {
                                    e.preventDefault()
                                    handleViewAll()
                                }}
                                style={{
                                    color: '#1976d2',
                                    textDecoration: 'none',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                }}
                            >
                                View All &gt;
                            </a>
                        </FlexRowBetween>
                        {inspectionLoading && (
                            <LoaderBar content="Loading inspections..." />
                        )}
                        {!inspectionLoading && (
                            <AppTable
                                data={upcomingInspectionsData}
                                columns={upcomingInspectionsColumns}
                                hidePagination
                                emptyMessage="No upcoming inspections"
                            />
                        )}
                    </div>
                </div>
            </DesktopMode>

            <MobileMode>
                <div className="mb-3">
                    <FlexRowBetween className="mb-3">
                        <FlexRow
                            onClick={handleCancel}
                            className="align-items-center pointer"
                        >
                            <CaretLeft size={24} />
                        </FlexRow>
                        <div
                            onClick={handleCancel}
                            style={{
                                color: '#1976d2',
                                fontSize: '14px',
                                cursor: 'pointer',
                            }}
                        >
                            Cancel
                        </div>
                    </FlexRowBetween>

                    <h2 style={{ marginBottom: '8px', fontWeight: 600 }}>
                        Schedule Asset Inspection
                    </h2>
                    <p
                        style={{
                            color: '#666',
                            fontSize: '14px',
                            marginBottom: '20px',
                        }}
                    >
                        Setup an inspection for one or more assets.
                    </p>

                    <Form noValidate>
                        <Form.Group>
                            <FormDropdownField
                                control={control}
                                name="AssetName"
                                label="Asset Name"
                                placeholder="Select Asset"
                                errors={errors.AssetName}
                                options={assetOptions}
                                required
                                disabled={!permissions.canScheduleInspection}
                            />
                        </Form.Group>
                        <Form.Group>
                            <FormDropdownField
                                control={control}
                                name="AssignedInspector"
                                label="Assigned Inspector"
                                placeholder="Select Inspector"
                                errors={errors.AssignedInspector}
                                options={inspectorOptions}
                                required
                                disabled={!permissions.canScheduleInspection}
                            />
                        </Form.Group>
                        <Form.Group>
                            <FormDropdownField
                                control={control}
                                name="InspectionType"
                                label="Inspection Type"
                                placeholder="Select Inspection Type"
                                errors={errors.InspectionType}
                                options={inspectionTypeOptions}
                                required
                                disabled={!permissions.canScheduleInspection}
                            />
                        </Form.Group>
                        <Form.Group>
                            <FormDateField
                                control={control}
                                name="InspectionDate"
                                label="Inspection Date"
                                errors={errors.InspectionDate}
                                type={DatePickerTypes.Single}
                                required
                                disabled={!permissions.canScheduleInspection}
                            />
                        </Form.Group>
                        <Form.Group>
                            <FormDateField
                                control={control}
                                name="DueDate"
                                label="Due Date"
                                errors={errors.DueDate}
                                type={DatePickerTypes.Single}
                                required
                                disabled={!permissions.canScheduleInspection}
                            />
                        </Form.Group>
                        <Form.Group>
                            <FormTextAreaFormField
                                control={control}
                                name="Notes"
                                label="Notes"
                                placeholder="Add any additional notes here."
                                errors={errors.Notes}
                                height={80}
                                disabled={!permissions.canScheduleInspection}
                            />
                        </Form.Group>
                        <Form.Group>
                            <label style={{ fontWeight: 600, marginBottom: 8 }}>
                                Media
                            </label>
                            <FileUploadPicker
                                files={mediaFiles}
                                maxFiles={10}
                                remove
                                onRemove={handleMediaRemove}
                                addedNewFiles={handleMediaUpload}
                                disabled={!permissions.canScheduleInspection}
                                uploadButtonText="Upload Picture"
                            />
                        </Form.Group>
                        <div className="mt-3">
                            <AppPrimaryButton
                                onClick={handleSubmit(submit)}
                                loading={loading}
                                fluid
                                disabled={!permissions.canScheduleInspection}
                            >
                                {editingInspectionId
                                    ? 'Update Inspection'
                                    : 'Schedule Inspection'}
                            </AppPrimaryButton>
                        </div>
                    </Form>

                    <div className="my-4">
                        <FlexRowBetween style={{ marginBottom: '12px' }}>
                            <h3
                                style={{
                                    margin: 0,
                                    fontSize: '18px',
                                    fontWeight: 600,
                                }}
                            >
                                Upcoming Inspections
                            </h3>
                            <a
                                href="#"
                                onClick={e => {
                                    e.preventDefault()
                                    handleViewAll()
                                }}
                                style={{
                                    color: '#1976d2',
                                    textDecoration: 'none',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                }}
                            >
                                View All &gt;
                            </a>
                        </FlexRowBetween>
                        {inspectionLoading && (
                            <LoaderBar content="Loading inspections..." />
                        )}
                        {!inspectionLoading && (
                            <AppTable
                                data={upcomingInspectionsData}
                                columns={upcomingInspectionsColumns}
                                hidePagination
                                emptyMessage="No upcoming inspections"
                            />
                        )}
                    </div>
                </div>
            </MobileMode>
        </ScheduleInspectionContainer>
    )
}
